import React, { useEffect, useState } from "react";
import { useAxios } from "../../../common_nextgen/axios/AxiosHelper";
import useCustomNavigate from "../../../common_nextgen/common_components/Navigation/NextGenNavigation";
import getBackendUrl from "../../../environment/BackendService";
import { fetchDropdownValues } from "../../../common_nextgen/common_components/dropdown/DropdownHelper";
import { useLocation } from "react-router-dom";
import { ROUTE_PATHS } from "../../../common_nextgen/utils/Constants";
import { useToast } from "../../../common_nextgen/hooks/ToastContext";
import { downloadCSV } from "../../../common_nextgen/utils/UtilFunctions";

const useMinMaxDifferential = () => {
  const { axios_helper_no_header } = useAxios();
  const [data, setData] = useState([]); /* Client Patient Pay Schedule Data */
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [rowData, setRowData] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);
  const navigateToPage = useCustomNavigate();
  const [dropdownValues, setDropdownValues] = useState({});
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState("no");
  const toast = useToast();
  const location = useLocation();
  const stateVal = location.state;
  const [currentState = {}] = [...stateVal].reverse();
  const previousState = [...stateVal];
  previousState.splice(stateVal.length - 1, 1);
  const {
    selectedSchedules = [],
    previousScreen = ROUTE_PATHS.PATIENT_PAY_TABLE,
    currentIndex: initialIndex = 0,
    paramsData: initialParamsData = {
      dmmPatientPayTable: selectedSchedules[initialIndex].ps4PtPaySchTableName,
      ps4PatPayDescription:
        selectedSchedules[initialIndex].ps4PatPayDescription,
      dmmStepNbr: "",
    },
  } = currentState;
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [paramsData, setParamsData] = useState(initialParamsData); /* */

  const selectedSchedulesData = selectedRows.length ? selectedRows : [rowData];
  const lastScheduleName =
    selectedSchedulesData[selectedSchedulesData.length - 1]?.dmmStepNbr;
  const updatedParamsData = {
    ...paramsData,
    dmmStepNbr: lastScheduleName,
  };

  const screen_title = "Minimum/Maximum Differential Step";
  const screenNumber = "RCDMM002";

  const formFields = [
    {
      fieldLabel: "DMM Patient Pay Table",
      name: "dmmPatientPayTable",
      label: "Patient Pay Table",
      type: "text",
      fieldNature: "readOnly",
      value: "",
      maxLength: 13,
    },
    {
      fieldLabel: "PS4 Pat Pay Description",
      name: "ps4PatPayDescription",
      label: "Description",
      type: "text",
      fieldNature: "readOnly",
      value: "",
      maxLength: 25,
    },
    {
      fieldLabel: "DMM Step Nbr",
      name: "dmmStepNbr",
      label: "Step",
      type: "text",
      fieldNature: "editable",
      value: "",
      maxLength: 2,
    },
  ];

  const dropdownOptions = [];

  const dropdownCondition = {
    dropDowns: dropdownOptions.map((item) => item.field_label + item.condition),
  };

  const leftButtons = [
    {
      button_label: "Back",
      button_handeler: () => {
        if (currentIndex < selectedSchedules.length - 1) {
          setCurrentIndex(currentIndex + 1);
        } else {
          navigateToPage(previousScreen, previousState);
        }
      },
      button_key: "F12",
    },
  ];

  const ctaButtons = [
    {
      button_label: "Add Min/Max Differential Step",
      button_handeler: () => {
        navigateToPage(ROUTE_PATHS.ADD_MINMAX_DIFF_STEP, [
          ...previousState,
          { ...currentState, currentIndex },
          {
            previousScreen: location.pathname,
            paramsData,
          },
        ]);
      },
      button_key: "F6",
    },
    {
      button_label: "Export as CSV",
      button_handeler: () => {
        exportAsCSV();
      },
      button_key: "",
    },
  ];

  const menuOptions = [
    {
      label: "Edit",
      icon: <span className="material-icons">edit</span>,
      command: () => {
        navigateToPage(ROUTE_PATHS.EDIT_MINMAX_DIFF_STEP, [
          ...previousState,
          {
            ...currentState,
            paramsData: updatedParamsData,
            currentIndex: currentIndex,
          },
          {
            selectedCopaySteps: selectedRows.length ? selectedRows : [rowData],
            previousScreen: location.pathname,
            paramsData: updatedParamsData,
          },
        ]);
      },
    },
    {
      label: "Delete",
      icon: <span className="material-icons">delete</span>,
      command: () => {
        openConfirmationModal();
      },
    },
    {
      label: "Display",
      icon: <span className="material-icons">visibility</span>,
      command: () => {
        navigateToPage(ROUTE_PATHS.DISPLAY_MINMAX_DIFF_STEP, [
          ...previousState,
          {
            ...currentState,
            paramsData: updatedParamsData,
            currentIndex: currentIndex,
          },
          {
            selectedCopaySteps: selectedRows.length ? selectedRows : [rowData],
            previousScreen: location.pathname,
            paramsData: updatedParamsData,
          },
        ]);
      },
    },
  ];

  const columnDef = [
    {
      header: "Step",
      field: "dmmStepNbr",
    },
    {
      header: "Step Days Supply",
      field: "dmmDaysSupply",
    },
    {
      header: "Step Minimum Value",
      field: "dmmDifferentialMin",
    },
    {
      header: "Step Maximum Value",
      field: "dmmDifferentialMax",
    },
  ];

  const parmKey = "dmmStepNbr";

  const fetchData = async () => {
    const page = first / rows;
    try {
      const raw_response = await axios_helper_no_header(
        "GET",
        `api/dmmdofferentialminmaxpricing`,
        null,
        {
          page: page,
          size: rows,
          ...paramsData,
        }
      );
      const response = raw_response.responseData;
      const pageResponse = raw_response.responsePaginationDTO;
      if (response.length === 0) {
        setData([]);
        setTotalRecords(0);
        toast.current.show({
          severity: "info",
          summary: "",
          detail: response.responseStatus.message,
          life: 3000,
        });
      } else {
        setData(response);
        setTotalRecords(pageResponse.totalRecords);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setData([]);
      setTotalRecords(0);
    }
  };

  const fetchDropdownData = async (dataDropdown) => {
    try {
      const response = await fetchDropdownValues(dataDropdown);
      setDropdownValues(response);
    } catch (error) {
      console.error("Error fetching dropdown values:", error);
    }
  };

  useEffect(() => {
    fetchDropdownData({
      dropDowns: dropdownOptions.map(
        (item) => item.field_label + item.condition
      ),
    });
  }, []);

  useEffect(() => {
    if (currentIndex !== initialIndex) {
      setParamsData({
        dmmPatientPayTable:
          selectedSchedules[currentIndex].ps4PtPaySchTableName,
        ps4PatPayDescription:
          selectedSchedules[currentIndex].ps4PatPayDescription,
        dmmStepNbr: "",
      });
    }
  }, [currentIndex, selectedSchedules]);

  useEffect(() => {
    fetchData();
  }, [paramsData, rows, currentPage]);

  const onPageChange = (event) => {
    setFirst(event.first);
    setCurrentPage(event.page);
    setRows(event.rows);
  };

  const openConfirmationModal = () => {
    setIsConfirmationModalOpen(true);
  };

  const closeConfirmationModal = () => {
    setIsConfirmationModalOpen(false);
    setSelectedOption("no");
  };

  const deletePriceSchedule = () => {
    if (selectedOption === "yes") {
      const params = {
        dmmPatientPayTable: paramsData.dmmPatientPayTable,
        dmmStepNbr: rowData.dmmStepNbr,
      };
      axios_helper_no_header(
        "DELETE",
        "api/dmmdofferentialminmaxpricing",
        null,
        params
      )
        .then((response) => {
          const updatedItems = data.filter(
            (item) => item.schedule !== rowData.schedule
          );
          if (currentPage > 0) {
            setFirst(0);
            setCurrentPage(0);
          } else {
            fetchData();
          }
          toast.current.show({
            severity: "success",
            summary: "",
            detail: response.responseStatus.message,
            life: 3000,
          });
          closeConfirmationModal();
        })
        .catch((error) => {
          console.error("There was an error deleting the data!", error);
          const errorMessage =
            error.data?.responseStatus?.message || error.message;
          toast.current.show({
            severity: "error",
            summary: "",
            detail: errorMessage,
            life: 3000,
          });
          closeConfirmationModal();
        });
    } else {
      closeConfirmationModal();
    }
  };

  const exportAsCSV = async () => {
    const queryParams = new URLSearchParams({ ...paramsData });

    try {
      const response = await axios_helper_no_header(
        "GET",
        "api/dmmdofferentialminmaxpricing/exportcsv",
        null,
        queryParams,
        "blob"
      );
      downloadCSV(response);
    } catch (error) {
      console.error("Error exporting as CSV:", error);
      toast.current.show({
        severity: "error",
        summary: "",
        detail: "Failed to export CSV. Please try again.",
        life: 3000,
      });
    }
  };

  const updateParamData = (data) => {
    setFirst(0);
    setCurrentPage(0);
    setParamsData(data);
  };

  return {
    screenNumber,
    screen_title,
    formFields,
    dropdownValues,
    paramsData,
    updateParamData,
    ctaButtons,
    data,
    parmKey,
    menuOptions,
    columnDef,
    setRowData,
    selectedRows,
    setSelectedRows,
    first,
    rows,
    totalRecords,
    currentPage,
    onPageChange,
    isConfirmationModalOpen,
    closeConfirmationModal,
    deletePriceSchedule,
    setSelectedOption,
    leftButtons,
  };
};

export default useMinMaxDifferential;
